import { useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatDate } from "@/lib/format";

const FLAG_META = {
  ok: { label: "OK", className: "text-nhp-primary border-nhp-primary/40 bg-nhp-primary/10" },
  orphaned_bypass: { label: "ORPHANED BYPASS", className: "text-nhp-critical border-nhp-critical/40 bg-nhp-critical/10" },
  missing_bypass: { label: "MISSING BYPASS", className: "text-nhp-warning border-nhp-warning/40 bg-nhp-warning/10" },
  expired: { label: "EXPIRED", className: "text-nhp-muted border-nhp-border" },
  unmanaged: { label: "UNMANAGED", className: "text-nhp-muted border-nhp-border" },
};

export default function LiveDevices() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: resp } = await api.get("/admin/live-devices");
      setData(resp);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    setLoading(false);
  };
  useEffect(() => { load(); const iv = setInterval(load, 15000); return () => clearInterval(iv); }, []);

  const devices = data?.devices || [];
  const offline = data?.offline_routers || [];
  const unreachable = data?.unreachable_routers || [];
  const problems = devices.filter((d) => d.flag === "orphaned_bypass" || d.flag === "missing_bypass").length;

  return (
    <div>
      <PageHeader
        testId="live-devices-header"
        title="Live Devices"
        subtitle="Every device every router currently sees, cross-checked against subscriptions"
        actions={
          <Button size="sm" variant="outline" onClick={load} disabled={loading} className="rounded-none font-mono text-xs h-9 gap-2">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            REFRESH
          </Button>
        }
      />

      <div className="p-4 md:p-8 space-y-4">
        {(offline.length > 0 || unreachable.length > 0) && (
          <div className="border border-nhp-warning/40 bg-nhp-warning/10 px-4 py-3 text-xs font-mono text-nhp-warning flex items-start gap-2" data-testid="live-devices-coverage-warning">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <div>
              {offline.length > 0 && <div>Skipped (marked offline): {offline.map((r) => r.name).join(", ")}</div>}
              {unreachable.length > 0 && <div>Could not reach: {unreachable.map((r) => r.name).join(", ")} -- data below is incomplete for these.</div>}
            </div>
          </div>
        )}

        {problems > 0 && (
          <div className="border border-nhp-critical/40 bg-nhp-critical/10 px-4 py-3 text-xs font-mono text-nhp-critical" data-testid="live-devices-problem-count">
            {problems} device{problems === 1 ? "" : "s"} flagged below -- bypassed with no active subscription, or paying but not bypassed.
          </div>
        )}

        <div className="border border-nhp-border bg-nhp-surface">
          <div className="hidden md:grid grid-cols-[repeat(12,minmax(0,1fr))] px-6 py-3 border-b border-nhp-border text-[10px] uppercase tracking-[0.2em] text-nhp-muted font-mono">
            <div className="col-span-2">MAC / IP</div>
            <div className="col-span-2">Site / Router</div>
            <div className="col-span-2">Customer</div>
            <div className="col-span-2">Package</div>
            <div className="col-span-2">Expires</div>
            <div className="col-span-2">Status</div>
          </div>
          <div className="divide-y divide-nhp-border">
            {!loading && devices.length === 0 && (
              <div className="p-8 text-center text-sm text-nhp-muted">No devices currently seen on any router.</div>
            )}
            {devices.map((d) => (
              <div key={`${d.router_id}-${d.mac}`} data-testid={`live-device-row-${d.mac}`} className="flex flex-col gap-2 md:grid md:grid-cols-[repeat(12,minmax(0,1fr))] px-4 md:px-6 py-4 md:items-center hover:bg-nhp-bg">
                <div className="md:col-span-2 font-mono text-xs">
                  <div className="text-nhp-text">{d.mac}</div>
                  <div className="text-nhp-muted">{d.ip}</div>
                </div>
                <div className="md:col-span-2 font-mono text-xs">
                  <div className="text-nhp-text">{d.site_name}</div>
                  <div className="text-nhp-muted">{d.router_name}</div>
                </div>
                <div className="md:col-span-2 font-mono text-xs text-nhp-text">{d.customer_phone || "—"}</div>
                <div className="md:col-span-2 font-mono text-xs text-nhp-text">{d.package_name || "—"}</div>
                <div className="md:col-span-2 font-mono text-xs text-nhp-muted">{d.expires_at ? formatDate(d.expires_at) : "—"}</div>
                <div className="md:col-span-2">
                  <span className={`font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-1 border ${FLAG_META[d.flag]?.className || FLAG_META.unmanaged.className}`}>
                    {FLAG_META[d.flag]?.label || d.flag}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
